import React from "react";
import { gql } from "@apollo/client";
import useSwitch from "@react-hook/switch";
import { materialColors, MaterialColor } from "@syos-music/instruments";

import {
  styled,
  ButtonBase,
  Popover,
  Grid,
  Divider,
  Paper,
} from "@mui/material";

const Color = styled(MaterialColor)(({ theme }) => ({
  border: "none",
  width: theme.spacing(3),
  height: theme.spacing(3),
}));

const NoColor = styled(Paper)(({ theme }) => ({
  width: theme.spacing(3),
  height: theme.spacing(3),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "&::before": { content: "'?'" },
}));

const ColorButton = React.forwardRef(function ColorButton(
  { color, ...props },
  ref
) {
  return (
    <ButtonBase ref={ref} {...props}>
      {color ? <Color color={color} variant="preview-tooltip" /> : <NoColor />}
    </ButtonBase>
  );
});

export default function SoundShapingColorSelector({
  soundShaping,
  onChange,
  disabled,
  className,
}) {
  const buttonRef = React.useRef();
  const [open, toggle] = useSwitch();

  const handleChange = React.useCallback(
    (color) => {
      onChange?.(color);
      toggle();
    },
    [onChange, toggle]
  );

  const publicColors = materialColors
    .filter((c) => !c.private)
    .map((c) => c.key);
  const privateColors = materialColors
    .filter((c) => c.private && !c.disabled)
    .map((c) => c.key);

  return (
    <>
      <ColorButton
        ref={buttonRef}
        onClick={toggle}
        disabled={disabled}
        color={soundShaping.color}
      />

      <Popover
        open={open}
        onClose={toggle}
        anchorEl={buttonRef.current}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        disableRestoreFocus
        disablePortal
        PaperProps={{ sx: { maxWidth: 200, p: 1 } }}
      >
        <Grid container spacing={1} justifyContent="center">
          {privateColors.map((color) => (
            <Grid item key={color}>
              <ColorButton color={color} onClick={() => handleChange(color)} />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {publicColors.map((color) => (
            <Grid item key={color}>
              <ColorButton color={color} onClick={() => handleChange(color)} />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item>
            <ColorButton onClick={() => handleChange(null)} />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
}

SoundShapingColorSelector.fragments = {
  soundShaping: gql`
    fragment Orders_SoundShapingColorSelector_SoundShaping on SoundShaping {
      id
      color
    }
  `,
};
